// Percorso file: src/utils/quote.js
//
// LE QUOTE DELL'ANNATA — sede unica della regola con cui il browser sceglie
// DOVE stanno le quote di (persona, anno).
//
// ⚠️ MODULO PURO, come `utils/stagione.js` che importa: nessun React, nessun
// hook, nessun contesto. Lo leggono una pagina (UsersList), una finestra
// (UserPaymentsModal) e un'utilità che non è un componente (exportCsv): se
// portasse dentro una dipendenza di React, l'export non potrebbe usarlo.
//
// ⚠️ È IL CALCO DI `sedeDelleQuote` (backend/server.js), non una seconda
// regola. Il backend la applica in lettura e in scrittura insieme — è quello
// che impedisce alle due cose di divergere — e qui si ripete perché il
// browser non condivide codice col server, esattamente come `annoDiLista`
// ripete `annoTollerante`. Se la regola del backend cambia, cambia anche
// questa: sono due copie DICHIARATE di un'unica dottrina, non due dottrine.
//
// LA REGOLA, per l'anno guardato Y:
//   · `feesSeasons[Y]` se quella chiave c'è;
//   · altrimenti, e SOLO per il 2026, il campo piatto `fees`;
//   · altrimenti — anno diverso, chiave assente — nessuna quota.
//
// ⚠️ NESSUN RIPIEGO SUL CAMPO SENZ'ANNO FUORI DAL 2026, ed è il punto: un
// 2027 senza quote è un 2027 senza quote, non un 2027 che eredita il dovuto
// del 2026. È lo stesso verso già scritto in UsersList per `alertSeasons` e
// `playFootballSeasons`; qui il 2026 fa eccezione per una ragione precisa e
// temporanea — il campo piatto È la stagione 2026, e il re-home dei dati
// esistenti è materia della migrazione (fetta 3), non di questa lettura.

import { annoDiLista } from './stagione';

// L'anno di cui il campo piatto `fees` è la sede. Stesso valore, e stesso
// significato, di `STAGIONE_STORICA` nel backend: la PWA ha vissuto una sola
// stagione, e i documenti scritti prima di PAY-SEASON sono tutti di quella.
export const STAGIONE_STORICA = 2026;

// La forma di UN record di quote per chi lo LEGGE.
//
// ⚠️ SI GARANTISCE LA FORMA, NON SI CONVERTONO I VALORI. `payments` esce
// sempre come array — senza, ogni chiamante dovrebbe difendersi da solo prima
// di un `.reduce` — ma `totalDue` e gli importi escono GREZZI, come li porta
// il documento. Non è pigrizia: la card «Outstanding Fees» di UsersList ha
// una guardia dichiarata (`!Number.isFinite(rawDue)` → fuori dai totali) che
// esiste apposta per riconoscere una quota malformata. Un `Number()` messo
// qui la renderebbe cieca, e cambierebbe in silenzio due cifre del club che
// questa fetta non ha mandato di toccare.
//
// ⚠️ LO SPREAD, e non un elenco di quattro campi: un campo che questa
// funzione non conosce non deve sparire per strada.
const recordDiQuote = (grezzo) => {
  const q =
    grezzo && typeof grezzo === 'object' && !Array.isArray(grezzo) ? grezzo : {};

  return {
    ...q,
    payments: Array.isArray(q.payments) ? q.payments : []
  };
};

/**
 * Le quote della persona per l'annata guardata.
 *
 * Restituisce SEMPRE un oggetto: se l'annata non si sa, se la mappa non c'è o
 * se quell'anno non ha voce, il record è vuoto — cioè «nessuna quota per
 * quest'anno», che è un'informazione, non un errore.
 *
 * ⚠️ ANNATA IGNOTA (`anno` non intero) → RECORD VUOTO, la stessa guardia di
 * `playFootballDellAnnata` in UsersList: senza un anno la domanda «quanto
 * deve questa persona NELL'ANNO?» non ha oggetto, e inventarne uno vorrebbe
 * dire mostrare (o peggio, scrivere) le quote di un'annata che nessuno ha
 * scelto.
 *
 * ⚠️ LA CHIAVE SI CERCA CON `annoDiLista`, non con un `[String(anno)]` crudo.
 * La proiezione di lista normalizza le chiavi a intero-stringa, ma
 * `GET /users/:id` — la ricarica della finestra Payments — risponde col
 * DOCUMENTO GREZZO, dove una chiave "2027 " o 2027 numerico è possibile: il
 * metro del browser resta il suo, e vale su tutte e due le vie. È la stessa
 * scelta, e la stessa riga, di `playFootballDellAnnata` in UsersList.
 *
 * ⚠️⚠️ E PROPRIO PER QUESTO NON È UN CALCO ESATTO: È UN SUPERSET, dichiarato.
 * `sedeDelleQuote` cerca `hasOwnProperty(String(anno))` — STRETTO — e in
 * scrittura punta il `FieldPath` sulla chiave CANONICA. Finché tutte le
 * chiavi le scrive il backend il superset non fa danno: nascono tutte da
 * `String(<intero validato>)`, quindi canoniche, e le due ricerche trovano la
 * stessa voce.
 *
 * ⚠️ MA SE UNA CHIAVE NON CANONICA COMPARISSE sul documento — e l'unico modo
 * è una MIGRAZIONE che la scriva (fetta 3) — lettura e scrittura
 * DIVERGEREBBERO: qui si leggerebbe `feesSeasons["2027 "]`, il backend
 * scriverebbe in `feesSeasons["2027"]`, e alla ricarica comparirebbe la voce
 * canonica (JavaScript enumera per prime le chiavi che sono indici interi),
 * facendo SEMBRARE svaniti i pagamenti dell'altra — che invece sono ancora
 * lì. Il rimedio non è qui: **la migrazione deve scrivere solo chiavi
 * canoniche**, cioè `String(intero)`. Se un giorno non lo si potesse
 * garantire, questa ricerca va stretta come quella del backend — e le due
 * cose vanno decise insieme, non una per volta.
 *
 * @param {object} user il socio, dalla proiezione di lista o dal documento
 * @param {number|null} anno l'annata guardata
 * @returns {{totalDue?: *, specialDiscount?: *, discountNote?: *, payments: Array}}
 */
export const quoteDellAnnata = (user, anno) => {
  const vuoto = recordDiQuote(null);

  if (!Number.isInteger(anno)) return vuoto;

  const mappa = user?.feesSeasons;

  if (mappa && typeof mappa === 'object' && !Array.isArray(mappa)) {
    const chiave = Object.keys(mappa).find((k) => annoDiLista(k) === anno);

    // ⚠️ SI GUARDA LA PRESENZA DELLA CHIAVE, non la verità del valore —
    // `hasOwnProperty`, come fa `sedeDelleQuote`. Un'annata esistente ma
    // vuota È un'annata senza quote, e non deve ricadere sul campo piatto.
    if (chiave !== undefined) return recordDiQuote(mappa[chiave]);
  }

  if (anno === STAGIONE_STORICA) return recordDiQuote(user?.fees);

  return vuoto;
};

/**
 * Il totale versato in un record di quote. Una riga sola, ma vive qui perché
 * la scrivevano in quattro punti diversi (le colonne di riga, la card degli
 * incassi, il filtro del saldo, l'export) e una copia può divergere.
 *
 * ⚠️ `Number(p.amount)` resta senza difesa, come prima: un importo non
 * numerico produce ancora «$NaN». È un difetto PREESISTENTE, censito nella
 * card di UsersList e lasciato lì dove era; il rimedio sta a monte, nella
 * validazione in scrittura, che è del backend (fetta 1) e non di questa
 * lettura.
 */
export const totaleVersato = (quote) =>
  (quote?.payments || []).reduce((somma, p) => somma + Number(p?.amount), 0);
